import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ShieldCheck, ArrowLeft, KeyRound, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export const LoginPage = ({ role, redirectTo, title, subtitle }) => {
  const { auth, login } = useAuth();
  const navigate = useNavigate();

  if (auth?.role === role) return <Navigate to={redirectTo} replace />;

  const onLoggedIn = (data) => {
    if (data.role !== role) {
      toast.error(`This login is for ${role}s only`);
      return;
    }
    toast.success(`Welcome, ${data.username}`);
    // Open dashboard in a new window so store stays open in the previous tab
    const opened = window.open(redirectTo, "_blank", "noopener");
    if (!opened) navigate(redirectTo);
  };

  return (
    <div
      data-testid={`${role}-login-page`}
      className="min-h-screen bg-background flex items-center justify-center p-6"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>

        <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
          <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mb-5">
            <ShieldCheck className="w-6 h-6 text-accent-foreground" />
          </div>
          <div className="font-mono-ui text-[10px] uppercase tracking-widest text-accent mb-2">
            Village Farm Food
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold leading-tight">
            {title}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>

          <Tabs defaultValue="password" className="mt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="password" data-testid={`${role}-tab-password`}>
                <KeyRound className="w-3.5 h-3.5 mr-2" /> Password
              </TabsTrigger>
              <TabsTrigger value="otp" data-testid={`${role}-tab-otp`}>
                <Smartphone className="w-3.5 h-3.5 mr-2" /> OTP
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <PasswordForm role={role} onLoggedIn={onLoggedIn} login={login} />
            </TabsContent>
            <TabsContent value="otp">
              <OtpForm role={role} onLoggedIn={onLoggedIn} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const PasswordForm = ({ role, onLoggedIn, login }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await login(username, password);
      onLoggedIn(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-widest font-mono-ui">Username</Label>
        <Input
          data-testid={`${role}-username`}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          className="mt-2 h-11"
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest font-mono-ui">Password</Label>
          <button
            type="button"
            data-testid={`${role}-forgot-btn`}
            onClick={() => setForgotOpen(true)}
            className="text-xs text-accent hover:underline underline-offset-2"
          >
            Forgot password?
          </button>
        </div>
        <Input
          data-testid={`${role}-password`}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="mt-2 h-11"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${role}-submit`}
        className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
      >
        {submitting ? "Signing in..." : "Sign in"}
      </Button>

      <ForgotPasswordDialog role={role} open={forgotOpen} onClose={setForgotOpen} />
    </form>
  );
};

const OtpForm = ({ role, onLoggedIn }) => {
  const { setSession } = useAuth();
  const [username, setUsername] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendOtp = async () => {
    if (!username) return toast.error("Enter your username");
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/otp/request`, { username });
      setSent(true);
      // DEMO MODE — SMS not wired, show OTP directly
      toast.success(`OTP sent (demo): ${data.demo_otp}`, { duration: 8000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/otp/verify`, { username, otp });
      setSession(data);
      onLoggedIn(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={verify} className="mt-5 space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-widest font-mono-ui">Username</Label>
        <Input
          data-testid={`${role}-otp-username`}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={sent}
          required
          className="mt-2 h-11"
        />
      </div>

      {!sent ? (
        <Button
          type="button"
          onClick={sendOtp}
          disabled={busy}
          data-testid={`${role}-otp-send`}
          className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
        >
          {busy ? "Sending..." : "Send OTP"}
        </Button>
      ) : (
        <>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono-ui">
              6-digit OTP
            </Label>
            <Input
              data-testid={`${role}-otp-code`}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              inputMode="numeric"
              maxLength={6}
              required
              autoFocus
              className="mt-2 h-11 tracking-[0.4em] text-center font-mono-ui"
              placeholder="••••••"
            />
            <button
              type="button"
              data-testid={`${role}-otp-resend`}
              onClick={sendOtp}
              disabled={busy}
              className="mt-2 text-xs text-accent hover:underline underline-offset-2"
            >
              Resend OTP
            </button>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid={`${role}-otp-verify`}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
          >
            {busy ? "Verifying..." : "Verify & sign in"}
          </Button>
        </>
      )}
    </form>
  );
};

const ForgotPasswordDialog = ({ role, open, onClose }) => {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1);
    setUsername("");
    setCode("");
    setNewPassword("");
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/forgot-password`, { username });
      toast.success(`Reset code sent (demo): ${data.demo_code}`, { duration: 10000 });
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/auth/reset-password`, {
        username,
        code,
        new_password: newPassword,
      });
      toast.success("Password updated. Please sign in.");
      reset();
      onClose(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onClose(v);
        if (!v) reset();
      }}
    >
      <DialogContent data-testid={`${role}-forgot-dialog`} className="max-w-md bg-background">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {step === 1 ? "Forgot your password?" : "Reset your password"}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={requestCode} className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Enter your username and we'll send you a 6-digit reset code.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Username</Label>
              <Input
                data-testid={`${role}-forgot-username`}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
                className="mt-2 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid={`${role}-forgot-send`}
              className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
            >
              {busy ? "Sending..." : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={doReset} className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Enter the code we sent and choose a new password (min 6 characters).
            </p>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">Reset code</Label>
              <Input
                data-testid={`${role}-reset-code`}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                required
                autoFocus
                className="mt-2 h-11 tracking-[0.4em] text-center font-mono-ui"
                placeholder="••••••"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-ui">New password</Label>
              <Input
                data-testid={`${role}-reset-new-password`}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid={`${role}-reset-submit`}
              className="w-full h-11 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
            >
              {busy ? "Resetting..." : "Reset password"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
